import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { fg } from '@confluence/feature-gating';

import ObjectCommentContainerPreloadObjectCommentsQuery from '../components/__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';
import {
	buildPreloadObjectCommentsQueryVariables,
	PRELOAD_LOADING_FLAG,
} from '../components/helpers/ObjectCommentContainerHelpers.ts';
import type {
	ObjectCommentContainerPreloadObjectCommentsQuery$data,
	ObjectCommentContainerPreloadObjectCommentsQuery$variables,
} from '../components/__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';

/**
 * Standalone function to preload footer comments
 * This can be called outside of React components (e.g., in route preloaders)
 */
export const preloadObjectComments = async (
	contentId: string,
): Promise<{
	data: ObjectCommentContainerPreloadObjectCommentsQuery$data | null;
}> => {
	if (!contentId) {
		return Promise.resolve({ data: null });
	}

	// Clear the cache BEFORE load
	// Don't clear in SSR - if the preload times out there we won't have a way to overwrite this
	try {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			writeQueryInterop<
				ObjectCommentContainerPreloadObjectCommentsQuery$data,
				ObjectCommentContainerPreloadObjectCommentsQuery$variables
			>({
				query: ObjectCommentContainerPreloadObjectCommentsQuery,
				variables: buildPreloadObjectCommentsQueryVariables(
					contentId,
					fg('confluence_vp_conversation_data_frag_reader'),
					fg('confluence_vp_comment_threads_frag_reader'),
				),
				data: {
					comments: {
						nodes: [],
						totalCount: PRELOAD_LOADING_FLAG,
						__typename: 'PaginatedCommentList',
					},
				},
			});
		}
	} catch {
		// Do nothing, this is expected if the query is not in the cache
	}

	// Use query from preloader-tools to trigger the network-only query
	// This will fetch the data and update the cache automatically
	// The hook query will pick up the changes from cache
	const result = await query<
		ObjectCommentContainerPreloadObjectCommentsQuery$data,
		ObjectCommentContainerPreloadObjectCommentsQuery$variables
	>({
		query: ObjectCommentContainerPreloadObjectCommentsQuery,
		variables: buildPreloadObjectCommentsQueryVariables(
			contentId,
			fg('confluence_vp_conversation_data_frag_reader'),
			fg('confluence_vp_comment_threads_frag_reader'),
		),
		fetchPolicy: 'network-only',
	});

	// Return in the same format as queryInterop for compatibility
	return { data: result.data };
};
